import type { AreaSubstrate, OptionsDocument, QuoteDocument, QuoteProduct } from 'paintscout'
import { getProducts } from '../../../options/get-products/get-products'
import type { Product } from '../../../options'
import { calculateMaterial, getAreaSubstrateKey } from '../../area-substrates'
import type { UpdateableProduct } from '../../area-substrates'
import { filterArchived } from '../filter-item'
import { calculateQuote } from '../calculate'

export interface ProductSwap {
  fromProductKey: string
  toProductKey: string
  /**
   * What this swap is allowed to touch. Omitted — the Swap Products dialog — means the whole quote:
   * area substrates in every rate section, plus line items.
   *
   * Area substrates are scoped by rate section because `quote.areas[x].substrates` is keyed by it.
   * Line items carry no rate section, so they are opted in separately.
   */
  target?: {
    /** Rate sections whose area substrates may swap. Absent or empty means none. */
    sections?: string[]
    /** Whether line-item materials may swap. */
    lineItems?: boolean
  }
}

/**
 * Build a raw QuoteProduct (area-substrate shape) from a matched product library entry.
 */
export function buildRawQuoteProduct(matched: Product): QuoteProduct {
  return {
    product: {
      label: matched.label,
      name: matched.key,
      unitLabel: matched.unitLabel || 'gal',
      color: '',
      manufacturer: matched.manufacturer || ''
    },
    calculate: true,
    coverage_rate_value: matched.coverage || 0,
    coverage_rate_override: false,
    coverage_rate_override_value: 0,
    lnft_coverage_rate_value: matched.lnftCoverage || 0,
    lnft_coverage_rate_override: false,
    lnft_coverage_rate_override_value: 0,
    quantity_coverage_rate_value: matched.quantityCoverage || 0,
    quantity_coverage_rate_override: false,
    quantity_coverage_rate_override_value: 0,
    volume: 0,
    volume_override: false,
    volume_override_value: 0,
    price: matched.price || 0,
    price_override: false,
    price_override_value: 0,
    totalPrice: 0,
    totalPrice_override: false,
    totalPrice_override_value: 0,
    custom: false
  }
}

/**
 * Build a line-item material (UpdateableProduct shape) from a matched product library entry.
 *
 * Line-item materials use OverridableValue fields (coverage/price/totalPrice/etc.), unlike the
 * area-substrate QuoteProduct shape above. Emitting the wrong shape here (e.g. a raw number for
 * `price` or a missing `totalPrice`) causes the recalculation pipeline to throw — see
 * generate-product-description reading `material.totalPrice.default`. We mirror how the
 * "Change Product" dialog (MaterialsTable) builds a material and run it through calculateMaterial.
 */
export function buildLineItemMaterial(matched: Product, existing?: UpdateableProduct): UpdateableProduct {
  return calculateMaterial({
    ...matched,
    color: existing?.color ?? '',
    quantity: existing?.quantity ?? 1,
    coverage: { default: matched.coverage ?? 0, useCustom: false, custom: 0 },
    lnftCoverage: { default: matched.lnftCoverage ?? 0, useCustom: false, custom: 0 },
    quantityCoverage: { default: matched.quantityCoverage ?? 0, useCustom: false, custom: 0 },
    volume: { default: 0, useCustom: false, custom: 0 },
    price: { default: matched.price ?? 0, useCustom: false, custom: 0 },
    totalPrice: { default: 0, useCustom: false, custom: 0 }
  })
}

/**
 * Expand selected keys, resolving group keys into their child area/line item keys.
 */
function expandKeys(quote: QuoteDocument, keys?: string[]): Set<string> | null {
  if (!keys) return null
  const expanded = new Set<string>()
  for (const key of keys) {
    if (quote.groups?.[key]) {
      quote.groups[key].children?.forEach((childKey: string) => expanded.add(childKey))
    } else {
      expanded.add(key)
    }
  }
  return expanded
}

/**
 * Determine whether an area substrate falls within the selected scope.
 *
 * Selection keys differ by table view: in area view checkboxes produce area keys, while in
 * surface (substrate) view they produce the grouped substrate keys found in `quote.substrates`.
 * An area substrate is in scope when no filter is set, or when either its parent area
 * (area-view selection) or its grouped surface key (surface-view selection) was selected.
 */
export function isAreaSubstrateSelected(
  quote: QuoteDocument,
  areaKey: string,
  substrate: AreaSubstrate,
  keySet: Set<string> | null
): boolean {
  if (!keySet) return true
  if (keySet.has(areaKey)) return true
  return keySet.has(getAreaSubstrateKey(quote, substrate))
}

/**
 * Swap products on a quote. For each swap pair, finds all non-archived substrates
 * and line items that use the "from" product and replaces it with the "to" product.
 * When keys are provided, only operates on the specified areas/line items, and a swap's
 * own `sections` further limits it to those rate sections.
 * Performs a single recalculation at the end unless `calculate` is false.
 */
export function swapQuoteProducts(args: {
  quote: QuoteDocument
  options: OptionsDocument
  swaps: ProductSwap[]
  keys?: string[]
  calculate?: boolean
}): QuoteDocument {
  const { options, swaps, calculate = true } = args
  const quote = JSON.parse(JSON.stringify(args.quote)) as QuoteDocument
  const keySet = expandKeys(quote, args.keys)

  for (const swap of swaps) {
    if (!swap.fromProductKey || !swap.toProductKey) continue

    const matched = getProducts({ options }).find((p) => p.key === swap.toProductKey)
    if (!matched) continue

    const newProduct = buildRawQuoteProduct(matched)

    const swapsAreaSubstrates = !swap.target || !!swap.target.sections?.length
    const swapsLineItems = !swap.target || !!swap.target.lineItems

    // Swap products on area substrates (skip archived areas)
    if (quote.areas && swapsAreaSubstrates) {
      for (const areaKey of Object.keys(quote.areas)) {
        const area = quote.areas[areaKey]

        if (filterArchived(area)) continue
        if (!area.substrates) continue

        for (const section of Object.keys(area.substrates)) {
          if (swap.target && !swap.target.sections?.includes(section)) continue

          const subs = area.substrates[section]
          if (!subs) continue

          for (const substrate of subs) {
            if (!substrate) continue
            if (!isAreaSubstrateSelected(quote, areaKey, substrate, keySet)) continue

            // Legacy singular product (older quotes/templates) — swap in place, keep singular shape.
            // The calculators already price this field, so an in-place swap recalculates correctly.
            if (substrate.product?.product?.name === swap.fromProductKey) {
              substrate.product = {
                ...newProduct,
                volume: substrate.product.volume,
                volume_override: substrate.product.volume_override,
                volume_override_value: substrate.product.volume_override_value
              }
            }

            // Modern products array
            if (substrate.products) {
              substrate.products = substrate.products.map((p: QuoteProduct) => {
                if (p?.product?.name === swap.fromProductKey) {
                  return {
                    ...newProduct,
                    volume: p.volume,
                    volume_override: p.volume_override,
                    volume_override_value: p.volume_override_value
                  }
                }
                return p
              })
            }
          }
        }
      }
    }

    // Swap products on line items (skip archived line items)
    if (quote.lineItems && swapsLineItems) {
      for (const lineItemKey of Object.keys(quote.lineItems)) {
        const lineItem = quote.lineItems[lineItemKey]

        if (filterArchived(lineItem)) continue
        if (keySet && !keySet.has(lineItemKey)) continue
        if (!lineItem.materials) continue

        lineItem.materials = lineItem.materials.map((m: UpdateableProduct) => {
          if (m?.key === swap.fromProductKey) {
            return buildLineItemMaterial(matched, m)
          }
          return m
        })
      }
    }
  }

  return calculate ? calculateQuote({ quote, options }) : quote
}
